/**
 * ScatterChart — relationships between two metrics (e.g. CAC vs LTV, spend vs
 * ROAS). Each point is a dot; pass a `size` to make it a bubble (radius scales
 * with the third dimension — e.g. revenue, customers). Axis ranges pad ~10% so
 * bubbles never clip at the edges. A faint diagonal break-even reference helps
 * "is x ≷ y?" reads (CAC vs LTV). Hover a dot for its label + x/y values.
 */

import { useMemo, useRef, useState } from "react";
import { moneyFmt } from "./cards.js";

export interface ScatterPoint {
  x: number;
  y: number;
  /** Optional third dimension → bubble radius. Omit for uniform dots. */
  size?: number;
  label: string;
}

const W = 600;
const H = 300;
const PAD = { l: 56, r: 16, t: 14, b: 42 };
const IW = W - PAD.l - PAD.r;
const IH = H - PAD.t - PAD.b;
const DOT_R = 4.5; // uniform-dot radius
const BUBBLE_MIN = 5;
const BUBBLE_MAX = 22;

/** "Nice" axis bounds: pad the data range ~10% (more when bubbles are present
 *  so their radii don't clip), guarding against a zero-width range. */
function axisRange(vals: number[], padFrac: number): { lo: number; hi: number } {
  const min = Math.min(...vals);
  const max = Math.max(...vals);
  if (min === max) {
    const pad = Math.abs(min) * 0.1 || 1;
    return { lo: min - pad, hi: max + pad };
  }
  const pad = (max - min) * padFrac;
  return { lo: min - pad, hi: max + pad };
}

/** Three readable ticks across an axis range, rounded for display. */
function ticks(lo: number, hi: number): number[] {
  return [lo, (lo + hi) / 2, hi];
}

export function ScatterChart({
  points,
  xLabel,
  yLabel,
  xMoney = false,
  yMoney = false,
  xSuffix = "",
  ySuffix = "",
}: {
  points: ScatterPoint[];
  /** Axis titles (also drive tooltip field names). */
  xLabel: string;
  yLabel: string;
  /** Format the axis/tooltip values as money (moneyFmt). */
  xMoney?: boolean;
  yMoney?: boolean;
  /** Unit suffix appended to non-money values (e.g. "x", "%"). */
  xSuffix?: string;
  ySuffix?: string;
}): React.ReactElement {
  const [hover, setHover] = useState<number | null>(null);
  const svgRef = useRef<SVGSVGElement>(null);

  const hasSize = points.some((p) => p.size !== undefined);

  const fmtX = (v: number): string =>
    xMoney ? moneyFmt(v) : `${Math.round(v).toLocaleString()}${xSuffix}`;
  const fmtY = (v: number): string =>
    yMoney ? moneyFmt(v) : `${Math.round(v).toLocaleString()}${ySuffix}`;

  // Pad ranges more generously when bubbles are present so their radii (up to
  // BUBBLE_MAX px) don't clip past the plot edges.
  const padFrac = hasSize ? 0.12 : 0.08;
  const rangeX = useMemo(
    () => (points.length ? axisRange(points.map((p) => p.x), padFrac) : { lo: 0, hi: 1 }),
    [points, padFrac],
  );
  const rangeY = useMemo(
    () => (points.length ? axisRange(points.map((p) => p.y), padFrac) : { lo: 0, hi: 1 }),
    [points, padFrac],
  );

  // Bubble radius scale (sqrt so AREA — not radius — reads proportional).
  const sizeRange = useMemo(() => {
    const sizes = points.map((p) => p.size ?? 0);
    return { min: Math.min(...sizes), max: Math.max(...sizes) };
  }, [points]);

  if (points.length === 0) {
    return <div style={{ color: "var(--faint)", padding: "18px 0" }}>No data yet.</div>;
  }

  const sx = (v: number): number =>
    PAD.l + ((v - rangeX.lo) / (rangeX.hi - rangeX.lo)) * IW;
  const sy = (v: number): number =>
    PAD.t + IH - ((v - rangeY.lo) / (rangeY.hi - rangeY.lo)) * IH;

  const radius = (size?: number): number => {
    if (!hasSize || size === undefined) return DOT_R;
    const { min, max } = sizeRange;
    if (max <= min) return (BUBBLE_MIN + BUBBLE_MAX) / 2;
    const frac = Math.sqrt((size - min) / (max - min));
    return BUBBLE_MIN + frac * (BUBBLE_MAX - BUBBLE_MIN);
  };

  // Faint break-even diagonal (y = x), only when both axes share a comparable
  // domain — useful for CAC vs LTV reads. Clamp to the overlapping range so it
  // stays inside the plot.
  const diagLo = Math.max(rangeX.lo, rangeY.lo);
  const diagHi = Math.min(rangeX.hi, rangeY.hi);
  const showDiag = diagHi > diagLo;

  const xTicks = ticks(rangeX.lo, rangeX.hi);
  const yTicks = ticks(rangeY.lo, rangeY.hi);

  // Nearest-point hover within the SVG's user space.
  const onMove = (e: React.MouseEvent<SVGSVGElement>): void => {
    const rect = svgRef.current?.getBoundingClientRect();
    if (!rect) return;
    const px = ((e.clientX - rect.left) / rect.width) * W;
    const py = ((e.clientY - rect.top) / rect.height) * H;
    let best = -1;
    let bestD = Infinity;
    points.forEach((p, i) => {
      const dx = sx(p.x) - px;
      const dy = sy(p.y) - py;
      const d = dx * dx + dy * dy;
      if (d < bestD) {
        bestD = d;
        best = i;
      }
    });
    // Only snap when reasonably close to a point.
    setHover(bestD <= 40 * 40 ? best : null);
  };

  const hovered = hover === null ? null : points[hover] ?? null;

  return (
    <div style={{ position: "relative" }}>
      <svg
        ref={svgRef}
        viewBox={`0 0 ${W} ${H}`}
        width="100%"
        role="img"
        aria-label={`${yLabel} vs ${xLabel} scatter chart`}
        onMouseMove={onMove}
        onMouseLeave={() => setHover(null)}
        style={{ display: "block" }}
      >
        {/* Horizontal gridlines + Y ticks */}
        {yTicks.map((v, i) => (
          <g key={`y${i}`}>
            <line x1={PAD.l} y1={sy(v)} x2={W - PAD.r} y2={sy(v)} stroke="var(--grid)" />
            <text x={PAD.l - 6} y={sy(v) + 3} textAnchor="end" fill="var(--body-faint)" fontSize={10} className="dd-num">
              {fmtY(v)}
            </text>
          </g>
        ))}

        {/* Vertical gridlines + X ticks */}
        {xTicks.map((v, i) => (
          <g key={`x${i}`}>
            <line x1={sx(v)} y1={PAD.t} x2={sx(v)} y2={PAD.t + IH} stroke="var(--grid)" />
            <text x={sx(v)} y={H - PAD.b + 16} textAnchor="middle" fill="var(--body-faint)" fontSize={10} className="dd-num">
              {fmtX(v)}
            </text>
          </g>
        ))}

        {/* Break-even reference (y = x) — faint, behind the dots */}
        {showDiag && (
          <line
            x1={sx(diagLo)}
            y1={sy(diagLo)}
            x2={sx(diagHi)}
            y2={sy(diagHi)}
            stroke="var(--body-faint)"
            strokeWidth={1}
            strokeDasharray="4 4"
            opacity={0.4}
          />
        )}

        {/* Points — larger bubbles drawn first so small ones stay clickable */}
        {points
          .map((p, i) => ({ p, i, r: radius(p.size) }))
          .sort((a, b) => b.r - a.r)
          .map(({ p, i, r }) => (
            <circle
              key={`${p.label}-${i}`}
              cx={sx(p.x)}
              cy={sy(p.y)}
              r={r}
              fill="var(--accent)"
              fillOpacity={hasSize ? 0.55 : 0.85}
              stroke="var(--accent)"
              strokeWidth={hover === i ? 2 : 1}
              opacity={hover === null || hover === i ? 1 : 0.4}
              style={{ transition: "opacity .12s ease" }}
            />
          ))}

        {/* Axis titles */}
        <text x={PAD.l + IW / 2} y={H - 5} textAnchor="middle" fill="var(--body)" fontSize={11} fontWeight={600}>
          {xLabel}
        </text>
        <text
          x={-(PAD.t + IH / 2)}
          y={14}
          transform="rotate(-90)"
          textAnchor="middle"
          fill="var(--body)"
          fontSize={11}
          fontWeight={600}
        >
          {yLabel}
        </text>
      </svg>

      {hovered && hover !== null && (
        <div
          style={{
            position: "absolute",
            left: `${((sx(hovered.x) / W) * 100).toFixed(1)}%`,
            top: `${((sy(hovered.y) / H) * 100).toFixed(1)}%`,
            transform: `translate(${sx(hovered.x) > W / 2 ? "calc(-100% - 10px)" : "10px"}, -50%)`,
            background: "var(--surface)",
            border: "1px solid var(--border)",
            borderRadius: 8,
            boxShadow: "var(--shadow-pop)",
            padding: "8px 11px",
            fontSize: 12,
            pointerEvents: "none",
            whiteSpace: "nowrap",
            zIndex: 5,
          }}
        >
          <div style={{ color: "var(--text)", fontWeight: 600, marginBottom: 4 }}>{hovered.label}</div>
          <div style={{ color: "var(--body)" }}>
            {xLabel}: <b className="dd-num" style={{ color: "var(--num)" }}>{fmtX(hovered.x)}</b>
          </div>
          <div style={{ color: "var(--body)" }}>
            {yLabel}: <b className="dd-num" style={{ color: "var(--num)" }}>{fmtY(hovered.y)}</b>
          </div>
          {hasSize && hovered.size !== undefined && (
            <div style={{ color: "var(--body-faint)", fontSize: 11.5, marginTop: 2 }}>
              size: <span className="dd-num">{Math.round(hovered.size).toLocaleString()}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
